import { BannerPreview } from '@/atoms/BannerPreview';
import { BreadcrumbSchema } from '@/components/BreadcrumbSchema';
import { CardSize, CardType, TagPosition } from '@/components/Card';
import { Gallery } from '@/components/Gallery';
import { LocationMap } from '@/components/LocationMap';
import { PaginationRoutes } from '@/components/PaginationRoutes';
import { ProductSchema } from '@/components/ProductSchema';
import { MobileEffect, SliderBlock } from '@/components/SliderBlock';
import { BACKEND_URL, BLACK_COLOR, PROMENAD_CODE } from '@/constants';
import { useEnhancedRouter } from '@/hooks/useEnhancedRouter';
import { Layout } from '@/layout';
import { fetchData } from '@/services/fetchData';
import { LocationType, SettingsType } from '@/types';
import { buildBreadcrumbsForLocation } from '@/utils/buildBreadcrumbs';
import { getTextColor } from '@/utils/getTextColor';
import { parseText } from '@/utils/parseText';
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import { useEffect } from 'react';
import styles from './Location.module.scss';

const getEntertainmentsTitle = (
  count: number,
  locale?: string,
  customText?: string,
) => {
  if (customText) {
    return customText;
  }

  console.log('locale', locale);

  const lang = locale?.split('-')[0];
  const roundedCount = Math.floor(count / 5) * 5;

  if (count < 5) {
    if (lang === 'ru') {
      return 'Развлечения';
    }

    if (lang === 'en') {
      return 'Things to Do';
    }

    if (lang === 'zh') {
      return '娱乐';
    }

    if (lang === 'ar') {
      return 'ترفيه';
    }

    return 'Things to Do';
  }

  if (lang === 'ru') {
    return `Более ${roundedCount} развлечений`;
  }

  if (lang === 'en') {
    return `Over ${roundedCount} things to do`;
  }

  if (lang === 'ar') {
    return `المزيد من ${roundedCount} ترفيه`;
  }

  if (lang === 'zh') {
    return `更多 ${roundedCount} 娱乐`;
  }

  return `Over ${roundedCount} things to do`;
};

enum BLOCK_ARRAY_ELEMENT {
  Entertainments = 'entertainments',
  Trades = 'trades',
  Map = 'map',
  Banner = 'banner',
}

const DEFAULT_BLOCK_ARRAY = [
  BLOCK_ARRAY_ELEMENT.Entertainments,
  BLOCK_ARRAY_ELEMENT.Trades,
  BLOCK_ARRAY_ELEMENT.Map,
  BLOCK_ARRAY_ELEMENT.Banner,
];

const getBlockStructure = (blocks: string[]) => {
  if (blocks && Array.isArray(blocks)) {
    const blockMapping: { [key: string]: string } = {
      entertainments: BLOCK_ARRAY_ELEMENT.Entertainments,
      trades: BLOCK_ARRAY_ELEMENT.Trades,
      map: BLOCK_ARRAY_ELEMENT.Map,
      banner: BLOCK_ARRAY_ELEMENT.Banner,
    };

    const orderedBlocks = blocks
      .map((block) => blockMapping[block])
      .filter(Boolean);

    const missingBlocks = DEFAULT_BLOCK_ARRAY.filter(
      (block) => !orderedBlocks.includes(block),
    );

    return [...orderedBlocks, ...missingBlocks];
  }

  return DEFAULT_BLOCK_ARRAY;
};

const Location = ({
  content,
  menu,
  translations,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const itemData: LocationType = content.item;
  const backendHTML = { __html: itemData.text };
  const { locale } = useEnhancedRouter();

  useEffect(() => {
    const color =
      itemData.ourObject && itemData.ourObject.colorStyles
        ? itemData.ourObject.colorStyles
        : BLACK_COLOR;

    if (color) {
      const styleTag = document.createElement('style');
      styleTag.textContent = getTextColor(color);
      document.head.appendChild(styleTag);

      return () => {
        document.head.removeChild(styleTag);
      };
    }
  }, [itemData.ourObject]);

  const orderedBlocks = getBlockStructure(itemData.blocks);

  const breadcrumbs = buildBreadcrumbsForLocation({
    linkHome: menu.translations.common.linkHome,
    objectsText: menu.translations.common.objects,
    ourObject: itemData.ourObject,
    locationName: itemData.name,
    locationUrl: content.meta?.canonical,
  });

  const productSchemaImage =
    itemData?.galleryMob?.[0] ?? itemData?.gallery?.[0] ?? itemData?.picture;

  const ogImage =
    productSchemaImage &&
    (productSchemaImage.startsWith('http://') ||
    productSchemaImage.startsWith('https://')
      ? productSchemaImage
      : BACKEND_URL
        ? `${BACKEND_URL}${productSchemaImage}`
        : productSchemaImage);

  const metaWithOgImage = {
    ...content.meta,
    picture: ogImage || content.meta?.picture,
  };

  return (
    <Layout data={menu} meta={metaWithOgImage}>
      <ProductSchema
        microdata={{ name: itemData.name, image: productSchemaImage }}
        objectName={itemData?.ourObject?.name}
        description={itemData.text}
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <main className={styles.page}>
        <PaginationRoutes
          routes={[
            {
              link: itemData.code,
              text: itemData.name,
            },
          ]}
        />
        <Gallery
          galleryMob={itemData.galleryMob}
          gallery={itemData.gallery}
          title={itemData.name}
        />
        <div className="info" dangerouslySetInnerHTML={backendHTML} />
        {itemData.ourObject && itemData.ourObject.styles && (
          <div
            dangerouslySetInnerHTML={{ __html: itemData.ourObject.styles }}
          />
        )}
        {orderedBlocks.map((block) => {
          switch (block) {
            case 'entertainments':
              return (
                <>
                  {itemData.entertainment &&
                    itemData.entertainment.length > 0 && (
                      <SliderBlock
                        items={itemData.entertainment}
                        cardType={CardType.Panel}
                        button={translations.entertainmentsButton}
                        link="/entertainments"
                        size={CardSize.XS}
                        title={getEntertainmentsTitle(
                          itemData.entertainment.length,
                          locale,
                          translations.entertainmentsTitle,
                        )}
                        mobileEffect={MobileEffect.Cards}
                        tagPosition={TagPosition.Bottom}
                        noInfo
                      />
                    )}
                </>
              );
            case 'trades':
              return (
                <>
                  {itemData.trades && itemData.trades.length > 0 && (
                    <SliderBlock
                      items={itemData.trades}
                      cardType={CardType.Panel}
                      link={`/objects/${PROMENAD_CODE}`}
                      size={CardSize.XS}
                      title={parseText(translations.tradesTitle)}
                      mobileEffect={MobileEffect.Slider}
                    />
                  )}
                </>
              );
            case 'map':
              return (
                <>
                  {itemData.zoneLocation && (
                    <LocationMap
                      img={itemData.zoneLocation.picture}
                      file={itemData.zoneLocation.file}
                      mobileImg={itemData.zoneLocation.pictureMob}
                    />
                  )}
                </>
              );
            case 'banner':
              return (
                <>
                  {itemData.banner && (
                    <BannerPreview
                      image={itemData.banner.desk}
                      imageTablet={itemData.banner.tablet}
                      imageMobile={itemData.banner.mob}
                      link={itemData.banner.link}
                    />
                  )}
                </>
              );
            default:
              return null;
          }
        })}
      </main>
    </Layout>
  );
};

export async function getServerSideProps({
  params,
  locale,
}: GetServerSidePropsContext) {
  const paramsId = params?.location;

  const menu: SettingsType = await fetchData({
    url: `/settings/view`,
    locale,
  });

  const content = await fetchData({
    url: `/content/location/${paramsId}/view`,
    locale,
  });

  const isError = !content || content.code !== 200;

  if (isError) return { notFound: true };

  return {
    props: {
      content,
      menu,
      translations: menu.translations['/locations/[location]'],
    },
  };
}

export default Location;
